import React from 'react';
import { FieldValues, UseControllerProps, useController } from 'react-hook-form';

import Select, { SelectProps } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

export type TControlledInputProps<T extends FieldValues> = SelectProps<T> &
  UseControllerProps<T> & {
    values: number[];
    valueText?: string;
  };

export const SelectWrapper = <T extends FieldValues>({
  values,
  valueText,
  name,
  label,
  control,
  ...props
}: TControlledInputProps<T>) => {
  const { field } = useController<T>({ control, name });

  return (
    <FormControl fullWidth>
      <InputLabel
        shrink
        sx={{
          fontWeight: 400,
          fontSize: 14,
          fontStyle: 'normal',
          lineHeight: '26px',
          color: (theme) => theme.palette.primary.main,
          top: '-25px',
          left: '0px',
          transform: 'none',
        }}
      >
        {label}
      </InputLabel>
      <Select
        {...field}
        {...props}
        sx={{
          width: '100%',
          borderRadius: { lg: '8px', md: '6px', sm: '6px', xs: '6px' },
          border: 'none',
          maxHeight: { lg: '44px', md: '46px', sm: '46px', xs: '46px' },
          p: { lg: '9px 12px', md: '15px 16px', sm: '15px 16px', xs: '15px 16px' },
          '& .MuiSelect-select': {
            p: '9px 0',
            boxSizing: 'border-box',
            borderRadius: { lg: '8px', md: '6px', sm: '6px', xs: '6px' },
            maxHeight: { lg: '44px', md: '46px', sm: '46px', xs: '46px' },
            backgroundColor: 'transparent',
          },
          '& svg': {
            width: '24px',
            height: '24px',
            top: 'inherit',
            '& path': {
              fill: (theme) => theme.palette.primary.main,
            },
          },
          '&:hover .MuiSelect-select': {
            backgroundColor: 'transparent',
          },
          '&.Mui-focused .MuiSelect-select': {
            backgroundColor: 'transparent',
          },
        }}
        disableUnderline
        variant="filled"
        MenuProps={{
          sx: {
            '& .MuiPaper-root': {
              borderRadius: { lg: '12px', md: '6px', sm: '6px', xs: '6px' },
              boxShadow: (theme) => `0px 2px 24px 0px ${theme.palette.myShadow.menu}`,
              '& .MuiList-root': {
                padding: 0,
                '& li': {
                  fontSize: '12px',
                  pl: '12px',
                  minHeight: { lg: '42px', md: '31px', sm: '31px', xs: '31px' },
                },
              },
            },
          },
        }}
        IconComponent={KeyboardArrowDownIcon}
      >
        {values.map((el, index) => (
          <MenuItem key={index} value={el}>
            {el}
            {valueText ? ` ${valueText}` : null}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
